import { useEffect, useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { statsData } from '@/mocks/videos';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 麻花视频';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formData.delete('website_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9nhb6gbdkpk99j7gjj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const isSpam = responseText.includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk && !isSpam) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="container-page">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* Brand Introduction */}
          <section className="py-12">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于麻花视频
              </h1>
              <p className="text-sm text-foreground-500 mb-8">
                致力于为用户提供最优质的免费在线观影体验
              </p>
              <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20creative%20office%20space%20with%20large%20screens%20displaying%20video%20content%20collaborative%20team%20working%20warm%20lighting%20plants%20glass%20walls%20tech%20company%20environment%20professional%20photography&width=1600&height=686&seq=mahua-about-hero&orientation=landscape"
                  alt="麻花视频团队"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="prose prose-sm max-w-none text-left">
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  麻花视频成立于2020年，是一个专注于提供<strong>高清影视内容免费在线观看</strong>的综合视频平台。平台涵盖电视剧、电影、综艺、动漫等多个品类，汇集了海量优质内容资源，致力于为广大用户打造<strong>流畅、高清、免费</strong>的观影体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的团队由一群热爱影视、追求极致用户体验的伙伴组成。我们深知，在快节奏的现代生活中，一部好剧、一部好电影是放松身心、获取灵感的最佳方式。因此，我们不断优化平台技术，采用先进的CDN加速和智能视频编码技术，确保每一位用户都能享受到<strong>不缓冲、不卡顿</strong>的高清观影体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  麻花视频始终坚持<strong>用户至上</strong>的原则。无需注册、无需会员、无需付费——打开网站即可观看。我们相信，好的内容应该被更多人看到，这也是麻花视频一直以来的初心和使命。
                </p>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-12 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                平台数据
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {statsData.map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-xl bg-background-50">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-accent-100">
                      <i className={`${stat.icon} text-xl text-accent-600`}></i>
                    </div>
                    <div className="text-2xl font-heading font-bold text-foreground-900 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { name: '小明', comment: '麻花视频真的太方便了，不用注册就能看，画质还特别好，追剧必备！' },
                  { name: '小红', comment: '资源很全，更新也快。最喜欢的是分类很清晰，找剧很方便。' },
                  { name: '大力', comment: '用了两年多了，一直很稳定。不卡顿不缓冲，体验很好。推荐给朋友了！' },
                ].map((item) => (
                  <div key={item.name} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      &ldquo;{item.comment}&rdquo;
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                        {item.name.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-foreground-800">{item.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                联系我们
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground-800 mb-4">联系方式</h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">邮箱</p>
                        <p className="text-sm font-medium text-foreground-800">contact@mahuavideo.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电话</p>
                        <p className="text-sm font-medium text-foreground-800">400-888-6666</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">地址</p>
                        <p className="text-sm font-medium text-foreground-800">北京市朝阳区光华路9号</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">工作时间</p>
                        <p className="text-sm font-medium text-foreground-800">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground-800 mb-4">发送消息</h3>
                  <form
                    id="about-contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-3"
                  >
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的消息（最多500字）"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                        required
                      ></textarea>
                    </div>
                    <div className="field-website-alt">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息发送成功！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "关于麻花视频",
        "description": "麻花视频是一个专注于提供高清影视内容免费在线观看的综合视频平台，涵盖电视剧、电影、综艺、动漫等品类。",
        "url": "https://www.mahuavideo.com/about",
        "isPartOf": {
          "@type": "WebSite",
          "name": "麻花视频",
          "url": "https://www.mahuavideo.com/"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.mahuavideo.com/" },
            { "@type": "ListItem", "position": 2, "name": "关于我们" }
          ]
        }
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "麻花视频",
        "url": "https://www.mahuavideo.com/",
        "description": "麻花视频提供海量电视剧免费在线观看，涵盖最新电视剧全集、高清不缓冲播放。",
        "email": "contact@mahuavideo.com",
        "telephone": "400-888-6666",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "光华路9号",
          "addressLocality": "北京",
          "addressRegion": "朝阳区",
          "addressCountry": "CN"
        }
      }) }} />
    </>
  );
}